import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// LT-2342: the custom packaging bundle dropdown on the org-dashboard collection detail page
// (subdomain/collections/show), mounted only when @collection.require_custom_packaging_bundle?.
// See spec/features/gives/custom_packaging_collections_spec.rb for what is characterized and
// what is deliberately left unexercised, and why.
export default class extends Controller {
  static get targets() {
    return ["searchBox", "selectButton", "dropdownMenu", "dropdown", "loading", "searchResults", "hiddenBundleId", "quantity", "removeButton"]
  }

  static get classes() {
    return ["selected"]
  }

  connect() {
    this.searchTimer = null
    this.initializeFilterState()
  }

  disconnect() {
    clearTimeout(this.searchTimer)
  }

  // `data-custom-packaging-collections-selected-class` is never set on this page's markup, so this
  // always falls back to the literal "selected" it replaces -- declared through the Classes API
  // instead of hard-coded at the two call sites below.
  get selectedClassName() {
    return this.hasSelectedClass ? this.selectedClass : "selected"
  }

  // The hidden field is already server-rendered from current_build; this only nudges the shopping
  // page's "Custom Packaging" filter pill. `#filter_custom_packaging` renders only in the
  // /give/collections flow, so this is a no-op on this page.
  initializeFilterState() {
    if (!this.hiddenBundleIdTarget.value) {
      return
    }
    const filterPill = document.querySelector("#filter_custom_packaging")
    if (filterPill && !filterPill.checked) {
      filterPill.click()
    }
  }

  // Dead: nothing calls this. The only element that named it was a "Send this collection" link on
  // a fragment no page actually fetches - the product modals all go to
  // Subdomain::ProductsController#show instead.
  nextStep(event) {
    event.preventDefault()
    const collectionId = event.currentTarget.dataset.collection
    const customPackagingBundleId = this.hiddenBundleIdTarget.value
    const requireCustomPackaging = event.currentTarget.dataset.requireCustom
    const form = document.querySelector("form#giving-form")

    if (!form) {
      return
    }

    if (collectionId) {
      const collectionIdField = form.querySelector("#build_collection_id")
      if (collectionIdField) {
        collectionIdField.value = collectionId
      }
    }

    if (customPackagingBundleId) {
      const bundleIdField = form.querySelector("#build_custom_packaging_bundle_id")
      if (bundleIdField) {
        bundleIdField.value = customPackagingBundleId
      }
    }

    if (requireCustomPackaging && !this.hiddenBundleIdTarget.value) {
      this.dropdownTarget.querySelector(".form-group")?.classList.add("has-error")
      this.dropdownTarget.querySelector(".help-block")?.classList.remove("hidden")
      return false
    }

    form.submit()
  }

  debouncedSearch(event) {
    event.stopPropagation()
    clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => this.performSearch(), 500)
  }

  // Powers the legacy custom packaging dropdown search (gives/collections_controller.rb's
  // search_custom_packaging) -- a bare `render json:` whose body is built from
  // `render_to_string(partial:, formats: [:html])`, so the full Accept header is what this
  // action's translation owes; it doesn't branch on X-Requested-With.
  performSearch() {
    const text = this.searchBoxTarget.value
    const selectedCustomPackagingId = this.hiddenBundleIdTarget.value
    let url = `${this.searchBoxTarget.dataset.url}?q=${text}`

    if (this.hasLoadingTarget) {
      this.loadingTarget.style.display = ""
    }

    if (selectedCustomPackagingId) {
      url = url + `&selected=${selectedCustomPackagingId}`
    }

    const options = RequestHelper.defaultRequestOptions()
    options.headers = Object.assign({}, options.headers, { "Accept": "application/json, text/plain, */*" })

    fetch(url, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to search custom packaging bundles (${response.status})`)
        }
        return response.json()
      })
      .then((data) => {
        this.searchResultsTarget.querySelectorAll("li").forEach((li) => li.remove())
        // S2: server-rendered HTML from our own origin -- `data.html` is the JSON body of
        // gives/collections#search_custom_packaging, built by
        // render_to_string(partial: "gives/collections/custom_packaging/search_results", ...).
        // That partial escapes the only user-entered field it renders (`= h custom_packaging_bundle.name`)
        // and contains no raw/html_safe/!= and no inline :javascript block, so nothing in it can
        // execute a script tag even though insertAdjacentHTML (unlike jQuery .html()) would not run
        // one anyway. Acceptable as-is.
        this.searchResultsTarget.insertAdjacentHTML("beforeend", data.html)
        if (this.hasLoadingTarget) {
          this.loadingTarget.style.display = "none"
        }
      })
      .catch((error) => {
        console.warn(error) // eslint-disable-line no-console
        if (this.hasLoadingTarget) {
          this.loadingTarget.style.display = "none"
        }
      })
  }

  // Delegated from #custom-packaging-search-results: performSearch re-renders the list wholesale,
  // so a per-<li> binding would not survive a search.
  selectResult(event) {
    const li = event.target.closest(".custom-packaging-result")
    if (!li || !this.searchResultsTarget.contains(li)) {
      return
    }
    event.preventDefault()

    const url = this.dropdownTarget.dataset.url
    const customPackagingBundleId = li.dataset.id

    this.dropdownTarget.querySelector(".form-group")?.classList.remove("has-error")
    this.dropdownTarget.querySelector(".help-block")?.classList.add("hidden")

    const nameEl = li.querySelector(".custom-packaging-name")
    this.selectButtonTarget.querySelector(".text").innerHTML = nameEl ? nameEl.innerHTML : ""
    this.hiddenBundleIdTarget.value = customPackagingBundleId

    Array.from(li.parentElement.children).forEach((sibling) => {
      if (sibling !== li) {
        sibling.classList.remove(this.selectedClassName)
      }
    })
    li.classList.add(this.selectedClassName)

    const quantityText = this.quantityTarget.querySelector(".quantity-text")
    if (quantityText) {
      quantityText.textContent = `Available: ${li.dataset.quantity}`
    }
    this.quantityTarget.style.display = ""

    if (!url) {
      return
    }

    const options = RequestHelper.defaultPutOptions()
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    })
    options.body = JSON.stringify({ custom_packaging_bundle_id: customPackagingBundleId })

    fetch(url, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to update custom packaging (${response.status})`)
        }
        if (this.hasRemoveButtonTarget) {
          this.removeButtonTarget.style.display = ""
        }
      })
      .catch((error) => console.warn(error)) // eslint-disable-line no-console
  }

  removeSelection(event) {
    event.preventDefault()
    const url = this.dropdownTarget.dataset.url
    this.hiddenBundleIdTarget.value = ""

    const options = RequestHelper.defaultPutOptions()
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*",
    })
    options.body = JSON.stringify({ custom_packaging_bundle_id: null })

    fetch(url, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to remove custom packaging (${response.status})`)
        }
        const text = this.selectButtonTarget.querySelector(".text")
        text.textContent = text.dataset.placeholder
        if (this.hasRemoveButtonTarget) {
          this.removeButtonTarget.style.display = "none"
        }
        this.quantityTarget.style.display = "none"
      })
      .catch((error) => console.warn(error)) // eslint-disable-line no-console
  }
}
